import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import swal from "sweetalert";
import * as XLSX from "xlsx";
import { bulkAtomIdLookup } from "../../../services/adminApiService/individualApiService/IndividualAPiService";
import { getAllBlockList } from "../../../services/commonApiService/CommonApiService";
import { formatDateTime } from "../../../utils/dateTimeFormate";
import { formatStatus, getStatusColor } from "../../../utils/colorAsperStatus";
import TextMasked from "../../../utils/TextMasked";
import { LocationContext } from "../../../context/LocationContext";

// Upload a CSV/XLSX of Atom IDs (not the "Unique Id" / provisinal
// registration id column) and show which ones resolved to a registrant,
// and which didn't. Parses client-side with the same `xlsx` package the
// Organization module's ImportExcelModal already uses -- SheetJS reads
// CSV fine too, so no new dependency.
const ATOM_ID_HEADER_RE = /atom.?id/i;

const extractAtomIds = (rows) => {
  if (!rows.length) return [];
  const headers = Object.keys(rows[0]);
  const atomIdKey = headers.find((h) => ATOM_ID_HEADER_RE.test(h)) || headers[0];
  const ids = rows
    .map((r) => String(r[atomIdKey] ?? "").trim())
    .filter(Boolean);
  return Array.from(new Set(ids));
};

const BulkAtomIdLookupModal = ({ show, onHide, onViewRecord }) => {
  const fileInputRef = useRef(null);
  const [parsing, setParsing] = useState(false);
  const [looking, setLooking] = useState(false);
  const [result, setResult] = useState(null); // { found: [...], not_found: [...] }
  const [error, setError] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [blockFilter, setBlockFilter] = useState("");
  const [blockOptionsByDistrict, setBlockOptionsByDistrict] = useState({});
  const { districtList, fetchDistricts } = useContext(LocationContext);

  useEffect(() => {
    if (!districtList.length) fetchDistricts();
  }, []);

  useEffect(() => {
    setBlockFilter("");
  }, [districtFilter]);

  // All blocks across every district in the results, used for the block
  // name lookup (table cells) regardless of the current district filter.
  const allBlockOptions = React.useMemo(
    () => Object.values(blockOptionsByDistrict).flat(),
    [blockOptionsByDistrict]
  );

  // Dropdown options scoped to the selected district; falls back to every
  // district's blocks when "All District" is selected.
  const blockOptions = districtFilter
    ? blockOptionsByDistrict[districtFilter] || []
    : allBlockOptions;

  // Look up a result row's district by the city (district id) field --
  // don't rely on the API's refDistrict relation being populated.
  const districtName = (r) =>
    r.refDistrict?.name ||
    districtList.find((d) => String(d.id) === String(r.city))?.label ||
    "";

  const blockName = (r) =>
    allBlockOptions.find((b) => String(b.id) === String(r.block))?.label || "";

  // Results can span multiple districts, so blocks are fetched per district
  // actually present in the found results and kept grouped by district id --
  // the block dropdown then scopes to whichever district is selected.
  const loadBlockOptions = async (found) => {
    const districtIds = Array.from(new Set(found.map((r) => r.city).filter(Boolean)));
    if (!districtIds.length) {
      setBlockOptionsByDistrict({});
      return;
    }
    try {
      const responses = await Promise.all(districtIds.map((id) => getAllBlockList(id)));
      const byDistrict = {};
      districtIds.forEach((id, i) => {
        byDistrict[id] = (responses[i].data || []).map((b) => ({ id: b.id, label: b.name }));
      });
      setBlockOptionsByDistrict(byDistrict);
    } catch (err) {
      console.error("Error fetching block options:", err);
    }
  };

  const reset = () => {
    setResult(null);
    setError("");
    setDistrictFilter("");
    setBlockFilter("");
    setBlockOptionsByDistrict({});
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    reset();
    onHide();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validExt = /\.(csv|xls|xlsx)$/i;
    if (!validExt.test(file.name)) {
      setError("Please upload a .csv, .xls, or .xlsx file.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setError("");
    setResult(null);
    setParsing(true);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const workbook = XLSX.read(event.target.result, { type: "binary" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet);
        const atomIds = extractAtomIds(rows);

        if (!atomIds.length) {
          setError("No Atom IDs found in the uploaded file.");
          setParsing(false);
          return;
        }

        setParsing(false);
        setLooking(true);
        setDistrictFilter("");
        setBlockFilter("");
        const response = await bulkAtomIdLookup(atomIds);
        const data = response?.data?.data || { found: [], not_found: atomIds };
        setResult(data);
        loadBlockOptions(data.found || []);
      } catch (err) {
        setError(err?.response?.data?.message || "Failed to look up Atom IDs.");
      } finally {
        setParsing(false);
        setLooking(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };
    reader.readAsBinaryString(file);
  };

  const filteredFound = React.useMemo(() => {
    if (!result?.found) return [];
    return result.found
      .filter((r) => !districtFilter || String(r.city) === String(districtFilter))
      .filter((r) => !blockFilter || String(r.block) === String(blockFilter));
  }, [result, districtFilter, blockFilter]);

  const handleExport = () => {
    if (!filteredFound.length) return;
    const rows = filteredFound.map((r) => ({
      "Atom Id": r.atom_id,
      "Applicant Full Name": r.name,
      "Email": r.email,
      "Sport Name": r.refsport?.sport_name || "",
      "District": districtName(r),
      "Block": blockName(r),
      "Contact Number": r.mobile,
      "Registration Date": formatDateTime(r.created_at),
      "Status": r.status ? formatStatus(r.status) : "",
    }));
    const sheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Results");
    XLSX.writeFile(workbook, `atom_id_lookup_${Date.now()}.xlsx`);
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Bulk Atom Id Lookup</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted" style={{ fontSize: 13 }}>
          Upload a CSV or Excel file with a column of Atom IDs. One ID per row —
          any column name containing "Atom Id" is used, or the first column if there's only one.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="form-control mb-3"
          onChange={handleFileChange}
          disabled={parsing || looking}
        />

        {(parsing || looking) && (
          <div className="text-center py-3">
            <div className="spinner-border text-primary" role="status" />
          </div>
        )}

        {error && <div className="alert alert-danger">{error}</div>}

        {result && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
              <span style={{ fontWeight: 600 }}>
                Found {filteredFound.length}
                {districtFilter ? ` of ${result.found.length}` : ""} / {result.found.length + result.not_found.length}
              </span>
              <div className="d-flex align-items-center gap-2">
                <select
                  className="form-control form-control-sm"
                  style={{ width: 200 }}
                  value={districtFilter}
                  onChange={(e) => setDistrictFilter(e.target.value)}
                >
                  <option value="">All District</option>
                  {districtList.map((d) => (
                    <option key={d.id} value={d.id}>{d.label}</option>
                  ))}
                </select>
                <select
                  className="form-control form-control-sm"
                  style={{ width: 200 }}
                  value={blockFilter}
                  onChange={(e) => setBlockFilter(e.target.value)}
                  disabled={!blockOptions.length}
                >
                  <option value="">All Block</option>
                  {blockOptions.map((b) => (
                    <option key={b.id} value={b.id}>{b.label}</option>
                  ))}
                </select>
                {filteredFound.length > 0 && (
                  <Button size="sm" variant="outline-primary" onClick={handleExport}>
                    Export Results
                  </Button>
                )}
              </div>
            </div>

            {filteredFound.length > 0 && (
              <div className="table-responsive mb-3" style={{ maxHeight: 320, overflowY: "auto" }}>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Atom Id</th>
                      <th>Applicant Full Name</th>
                      <th>Email</th>
                      <th>Sport Name</th>
                      <th>District</th>
                      <th>Block</th>
                      <th>Contact Number</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFound.map((r) => (
                      <tr key={r.atom_id}>
                        <td>{r.atom_id}</td>
                        <td>{r.name}</td>
                        <td>{r.email}</td>
                        <td>{r.refsport?.sport_name || ""}</td>
                        <td>{districtName(r)}</td>
                        <td>{blockName(r)}</td>
                        <td><TextMasked value={r.mobile} type="phone" /></td>
                        <td>
                          <span style={{ color: getStatusColor(r.status) }}>
                            {r.status ? formatStatus(r.status) : ""}
                          </span>
                        </td>
                        <td>
                          <Button size="sm" onClick={() => onViewRecord?.(r.id)}>View</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {result.not_found.length > 0 && (
              <div className="alert alert-warning" style={{ fontSize: 13 }}>
                <strong>Not found ({result.not_found.length}):</strong>{" "}
                {result.not_found.join(", ")}
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkAtomIdLookupModal;
